
import React from 'react';
import { Sample, SampleStatus } from '../../types';
import { TestTube, Clock, CheckCircle, Archive, Printer, ArrowRight, FileText, Briefcase } from 'lucide-react';

interface SampleTableProps {
  samples: Sample[];
  onAction: (action: string, sample: Sample) => void;
}

const SampleTable: React.FC<SampleTableProps> = ({ samples, onAction }) => {

  const getStatusBadge = (sample: Sample) => {
    switch (sample.status) {
      case 'received': return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold flex items-center gap-1"><TestTube size={12}/> Reçu</span>;
      case 'processing': 
         const hasPartial = sample.results && sample.results.length > 0 && sample.results.some(r => r.result);
         if (hasPartial) {
             return <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-bold flex items-center gap-1"><FileText size={12}/> Partiel</span>;
         }
         return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-bold flex items-center gap-1"><Clock size={12}/> En cours</span>;
      case 'validated': return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold flex items-center gap-1"><CheckCircle size={12}/> Validé</span>;
      case 'archived': return <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-bold flex items-center gap-1"><Archive size={12}/> Archivé</span>;
    }
  };

  return (
    <div className="w-full">
        <table className="w-full text-left text-sm text-slate-600 border-collapse">
          <thead className="sticky top-0 z-10 bg-slate-100 text-slate-700 font-bold border-b-2 border-slate-200 shadow-sm">
            <tr>
              <th className="px-6 py-4 uppercase tracking-wider text-[11px]">ID / Date</th>
              <th className="px-6 py-4 uppercase tracking-wider text-[11px]">Patient</th>
              <th className="px-6 py-4 uppercase tracking-wider text-[11px]">Analyses</th>
              <th className="px-6 py-4 uppercase tracking-wider text-[11px]">Résultats</th>
              <th className="px-6 py-4 uppercase tracking-wider text-[11px]">Statut</th>
              <th className="px-6 py-4 uppercase tracking-wider text-[11px] text-right">Workflow</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {samples.map((sample) => (
              <tr key={sample.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-6 py-3">
                  <div className="font-mono text-xs text-slate-400">{sample.id.slice(0, 8)}...</div>
                  <div className="text-slate-900 font-medium">{new Date(sample.collection_date).toLocaleDateString()}</div>
                  <div className="text-xs text-slate-500">{sample.sample_type}</div>
                  {sample.projects && (
                      <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-100 text-indigo-700 text-[10px] font-bold">
                          <Briefcase size={10} /> {sample.projects.code || sample.projects.name}
                      </div>
                  )}
                </td>
                <td className="px-6 py-3">
                  {sample.patients ? (
                    <div>
                      <div className="font-bold text-slate-800">{sample.patients.last_name} {sample.patients.first_name}</div>
                      <div className="text-xs text-slate-500">{sample.patients.unique_id}</div>
                    </div>
                  ) : (
                    <span className="italic text-slate-400">Inconnu</span>
                  )}
                </td>
                <td className="px-6 py-3">
                    {sample.analyses && sample.analyses.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                            {sample.analyses.map(a => (
                                <span key={a} className="px-1.5 py-0.5 bg-indigo-50 text-indigo-700 text-[10px] font-semibold uppercase rounded border border-indigo-100">{a}</span>
                            ))}
                        </div>
                    ) : (
                        <span className="text-xs text-slate-400 italic">Aucune</span>
                    )}
                </td>
                <td className="px-6 py-3">
                    {sample.results && sample.results.length > 0 ? (
                         <div className="space-y-1">
                             {sample.results.map((r, i) => (
                                 <div key={i} className="text-xs">
                                     <span className="font-semibold text-slate-700">{r.analysis}:</span> <span className="text-slate-600">{r.result}</span>
                                 </div>
                             ))}
                         </div>
                    ) : (
                        <span className="text-xs text-slate-400 italic">-</span>
                    )}
                </td>
                <td className="px-6 py-3">
                  {getStatusBadge(sample)}
                </td>
                <td className="px-6 py-3 text-right">
                    <div className="flex justify-end gap-2 items-center">
                      {(sample.analyses && sample.analyses.length > 0) && (
                        <button 
                            onClick={() => onAction('report', sample)}
                            className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                            title="Imprimer Résultats"
                        >
                            <FileText size={16} />
                        </button>
                      )}

                      <button 
                        onClick={() => onAction('label', sample)}
                        className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                        title="Imprimer Étiquette"
                      >
                        <Printer size={16} />
                      </button>

                      {sample.status === 'received' && (
                        <button onClick={() => onAction('analyze', sample)} className="px-3 py-2 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-medium hover:bg-indigo-100 flex items-center gap-1 border border-indigo-100">
                          Analyser <ArrowRight size={12}/>
                        </button>
                      )}
                      {sample.status === 'processing' && (
                        <button onClick={() => onAction('validate', sample)} className="px-3 py-2 bg-emerald-50 text-emerald-600 rounded-lg text-xs font-medium hover:bg-emerald-100 flex items-center gap-1 border border-emerald-100">
                          Valider <CheckCircle size={12}/>
                        </button>
                      )}
                      {sample.status === 'validated' && (
                        <span className="px-3 py-1 bg-slate-100 text-slate-400 rounded-lg text-xs font-medium">
                          Terminé
                        </span>
                      )}
                    </div>
                </td>
              </tr>
            ))}
            {samples.length === 0 && (
                <tr><td colSpan={6} className="p-12 text-center text-slate-400 italic">Aucun prélèvement trouvé</td></tr>
            )}
          </tbody>
        </table>
    </div>
  );
};

export default SampleTable;
